<template>

    <div class="px-6 py-4 bg-minplan-beige">
        <page-title :title="$t('suppliers.editBase')"></page-title>
    </div>

    <jet-form-section @submitted="updateSupplier()">

        <template #form>

            <div class="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6 grid grid-cols-6 gap-4">

                <div class="col-span-6">
                    <label for="name" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.name") }}</label>
                    <input type="text" name="name" id="name" v-model="form.name"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                           autofocus />
                    <jet-input-error :message="form.errors.name" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="street" class="block text-sm font-medium text-gray-700">{{
                            $t("suppliers.street")
                        }}</label>
                    <input type="text" name="street" id="street" v-model="form.location.street"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['location.street']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-1">
                    <label for="zip" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.zip") }}</label>
                    <input type="text" name="zip" id="zip" v-model="form.location.zip"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300">
                    <jet-input-error :message="form.errors['location.zip']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                    <label for="city" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.city") }}</label>
                    <input type="text" name="city" id="city" v-model="form.location.city"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300">
                    <jet-input-error :message="form.errors['location.city']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                    <label for="city" class="block text-sm font-medium text-gray-700">{{
                            $t("suppliers.country")
                        }}</label>
                    <Dropdown v-model="form.location.country"
                              :options="countries"
                              optionValue="code"
                              optionLabel="name"
                              :placeholder="$t('forms.selectCountry')"
                              class="mt-1 w-full" />
                    <jet-input-error :message="form.errors['location.country']" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="website" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.website") }}
                        <span class="text-gray-400">({{ $t("forms.optional") }})</span></label>
                    <input type="text" name="website" id="website" v-model="form.website"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors.website" class="mt-2" />
                </div>

                <!-- Thema 9 (Welle 4, Paket c): Firmendaten (E-Mail/Telefon)
                     leben hier neben `website` — dieselbe Ebene "Unternehmer
                     als Ganzes", nicht die einzelne Kontaktperson. -->
                <div class="col-span-6 sm:col-span-3">
                    <label for="email" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.email") }}
                        <span class="text-gray-400">({{ $t("forms.optional") }})</span></label>
                    <input type="text" name="email" id="email" v-model="form.email"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors.email" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3">
                    <label for="phone" class="block text-sm font-medium text-gray-700">{{ $t("suppliers.phone") }}
                        <span class="text-gray-400">({{ $t("forms.optional") }})</span></label>
                    <input type="text" name="phone" id="phone" v-model="form.phone"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors.phone" class="mt-2" />
                </div>

            </div>
        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">

                <button
                    type="button"
                    @click="cancel" class="inline-block uppercase font-semibold text-xs tracking-widest">
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="ml-3 ">
                    {{ $t("actions.save") }}
                </primary-button>

            </div>
        </template>

    </jet-form-section>

</template>


<script>
import { defineComponent } from "vue";
import { countries } from "@/Shared/countries";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import PageTitle from "@/Shared/Components/PageTitle";
import { useForm } from "@inertiajs/inertia-vue3";

export default defineComponent({
    props: ["supplier"],
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton
    },
    data() {
        return {
            countries,
        };
    },
    methods: {
        cancel() {
            this.$emit("closeModal");
        },
        updateSupplier() {
            this.form.put(this.route("advisors.suppliers.update", this.supplier.uuid), {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: 'actions.success',
                    text: 'suppliers.updated'
                })
            });
        }
    },
    setup(props) {
        const form = useForm({
            name: props.supplier.name,
            number: props.supplier.number,
            website: props.supplier.website,
            email: props.supplier.email,
            phone: props.supplier.phone,
            location: {
                street: props.supplier.street,
                zip: props.supplier.zip,
                city: props.supplier.city,
                country: props.supplier.country
            }
        });

        return { form };
    }
});
</script>

<style scoped></style>
